<template>
  <div class="flex flex-col gap-4 text-left" data-testid="qualification-requirement-tab">
    <div v-if="isLoading" class="h-40 rounded-lg bg-gray-100 animate-pulse" />
    <p
      v-else-if="isUnavailable"
      class="text-sm text-[#52526f]"
      data-testid="qualification-requirement-unavailable"
    >
      A qualificação exigida ainda não foi liberada neste ambiente.
    </p>
    <p
      v-else-if="loadError"
      class="text-sm text-negative"
      role="alert"
      data-testid="qualification-requirement-load-error"
    >
      {{ loadError }}
    </p>
    <template v-else>
      <p class="text-sm text-[#52526f]" data-testid="qualification-requirement-current">
        {{ statusText }}
      </p>
      <QualificationRequirementSection
        v-model="policy"
        @validity-change="onValidityChange"
        @copied-from="onCopiedFrom"
      />
      <div class="max-w-[260px]">
        <Input
          label="Vale para plantões a partir de"
          required
          mask="##/##/####"
          width="100%"
          :value="validFrom"
          :is-error="!!validFromMessage"
          :error-message="validFromMessage ?? ''"
          data-testid="qualification-requirement-valid-from"
          @update:model-value="onValidFromInput"
        />
        <p class="mt-1 text-xs text-gray-500">
          Plantões antes desta data continuam com a regra anterior.
        </p>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useToast } from 'vue-toastification'

import Network from '@/Network'
import Input from '@/components/designSystem/Inputs/Input.vue'
import QualificationRequirementSection from '@/components/QualificationRequirement/QualificationRequirementSection.vue'
import {
  clonePolicy,
  earliestValidFrom,
  emptyPolicy,
  validFromError
} from '@/components/QualificationRequirement/policyModel'
import { useErrorToast } from '@/composables/useErrorToast'
import {
  parseBrDate,
  todayInSaoPaulo
} from '@/modules/company/Approvals/ProfessionalDocuments/model'
import type { QualificationRuleGroup, SchedulePolicyLink } from '@/types/qualification-policy.types'
import { getApiErrorMessage } from '@/utils/apiError'
import { formatIsoDateOnlyBR } from '@/utils/date'
import { isProfessionalDocumentsDisabled } from '@/utils/qualificationErrors'

const props = defineProps<{ scheduleId: number; companyId: number }>()
const emit = defineEmits<{ 'update:isSaving': [value: boolean] }>()

const toast = useToast()
const { notifyError } = useErrorToast()

const isLoading = ref(false)
const isUnavailable = ref(false)
const loadError = ref<string | null>(null)
const current = ref<SchedulePolicyLink | null>(null)
const history = ref<SchedulePolicyLink[]>([])
const policy = ref<QualificationRuleGroup>(emptyPolicy())
const originalSnapshot = ref(JSON.stringify(emptyPolicy()))
const isPolicyValid = ref(true)
/** Preenchido pelo "Copiar requisitos" da mesma empresa; vai no PUT e zera a cada carga. */
const copiedFromPolicyId = ref<number | null>(null)
const validFrom = ref('')
const validFromMessage = ref<string | null>(null)

/** Vínculo aberto (vigente ou agendado): é a versão editada, como o backend a substitui. */
const openLink = computed(() => history.value.find((link) => link.validTo === null) ?? null)
const scheduled = computed(() =>
  openLink.value && openLink.value.validFrom > todayInSaoPaulo() ? openLink.value : null
)
const previousCount = computed(() => {
  const today = todayInSaoPaulo()
  return history.value.filter((link) => link.validTo !== null && link.validTo < today).length
})

const statusText = computed(() => {
  const link = current.value
  const parts = [
    link
      ? `Vigente desde ${formatIsoDateOnlyBR(link.validFrom)}${link.validTo ? ` até ${formatIsoDateOnlyBR(link.validTo)}` : ''}.`
      : 'Esta escala ainda não exige qualificação.'
  ]
  if (scheduled.value)
    parts.push(
      `Nova versão a partir de ${formatIsoDateOnlyBR(scheduled.value.validFrom)}: é a que aparece abaixo.`
    )
  const count = previousCount.value
  if (count)
    parts.push(`${count} ${count === 1 ? 'versão anterior' : 'versões anteriores'} no histórico.`)
  return parts.join(' ')
})

const hasUnsavedChanges = computed(() => JSON.stringify(policy.value) !== originalSnapshot.value)
const isFormValid = computed(
  () => !isLoading.value && !isUnavailable.value && !loadError.value && isPolicyValid.value
)

async function load() {
  isLoading.value = true
  loadError.value = null
  try {
    const response = await Network.qualificationEligibility.getSchedulePolicy(props.scheduleId)
    current.value = response.current
    history.value = response.history
    policy.value = openLink.value ? clonePolicy(openLink.value.rules) : emptyPolicy()
    originalSnapshot.value = JSON.stringify(policy.value)
    copiedFromPolicyId.value = null
    validFrom.value = formatIsoDateOnlyBR(earliestValidFrom(response.history, todayInSaoPaulo()))
    validFromMessage.value = null
  } catch (error) {
    if (isProfessionalDocumentsDisabled(error)) {
      // fallback intencional: feature desligada no backend (R46.4) — estado próprio, sem toast.
      isUnavailable.value = true
      return
    }
    console.error('Erro ao carregar a qualificação exigida da escala:', error)
    loadError.value = getApiErrorMessage(
      error,
      'Não foi possível carregar a qualificação exigida da escala'
    )
  } finally {
    isLoading.value = false
  }
}

const onValidityChange = (valid: boolean) => (isPolicyValid.value = valid)

/** R51.4: a linhagem só vale na mesma empresa — o backend recusa origem de outra (404). */
function onCopiedFrom(sourcePolicyId: number, sourceCompanyId: number | null) {
  copiedFromPolicyId.value = sourceCompanyId === props.companyId ? sourcePolicyId : null
}

function onValidFromInput(value: string) {
  validFrom.value = value
  validFromMessage.value = null
}

/** Salvar = fechar o vínculo aberto e abrir outro a partir de `validFrom` (D61). Raiz vazia remove a exigência. */
async function handleSave(): Promise<void> {
  if (!isFormValid.value) return
  const today = todayInSaoPaulo()
  validFromMessage.value = validFromError(
    validFrom.value,
    today,
    earliestValidFrom(history.value, today)
  )
  const validFromIso = parseBrDate(validFrom.value)
  if (validFromMessage.value || !validFromIso) return
  emit('update:isSaving', true)
  try {
    await Network.qualificationEligibility.putSchedulePolicy(props.scheduleId, {
      rules: policy.value,
      validFrom: validFromIso,
      ...(copiedFromPolicyId.value !== null ? { copiedFromPolicyId: copiedFromPolicyId.value } : {})
    })
    toast.success(
      `Qualificação exigida salva. Vale para plantões a partir de ${formatIsoDateOnlyBR(validFromIso)}.`
    )
    await load()
  } catch (error) {
    notifyError(error, 'Não foi possível salvar a qualificação exigida da escala')
  } finally {
    emit('update:isSaving', false)
  }
}

onMounted(load)

defineExpose({ handleSave, hasUnsavedChanges, isFormValid })
</script>
